import { ArcElement, Chart as ChartJS, ChartData, ChartOptions, DoughnutController } from "chart.js"
import { Box, Stack, Typography } from "@mui/material"
import { useMemo } from "react"
import { Doughnut } from "react-chartjs-2"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

import { heroFontFamily } from "./heroFont"
import type { TenantLegendItem } from "./tenantUsageViewModel"

ChartJS.register(DoughnutController, ArcElement)

// Non-hovered arcs are faded to ~25% opacity (chart.js needs a raw color string; we derive
// it from the token hex by appending an alpha channel, so the hue/value are preserved).
const FADED_ALPHA = "40"

const useStyles = makeStyles()((theme) => ({
    wrapper: {
        position: "relative",
        width: "12rem",
        height: "12rem",
    },
    center: {
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        textAlign: "center",
    },
    centerValue: {
        fontFamily: heroFontFamily,
        fontSize: "1.75rem",
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    centerCaption: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[500],
    },
}))

type TenantUsageDonutProps = Readonly<{
    legend: ReadonlyArray<TenantLegendItem>
    centerValue: string
    hoveredIndex: number | null
    onHoverIndex: (index: number | null) => void
}>

export function TenantUsageDonut({ legend, centerValue, hoveredIndex, onHoverIndex }: TenantUsageDonutProps) {
    const { classes } = useStyles()

    const data = useMemo<ChartData<"doughnut">>(() => {
        // On hover, fade every non-hovered arc (keeping its hue) so the hovered tenant stands out.
        const colors = legend.map((item, index) =>
            hoveredIndex === null || hoveredIndex === index ? item.color : `${item.color}${FADED_ALPHA}`,
        )

        return {
            labels: legend.map((item) => item.name),
            datasets: [
                {
                    data: legend.map((item) => item.percentage),
                    backgroundColor: colors,
                    borderColor: colors,
                    borderWidth: 0,
                    hoverOffset: 0,
                },
            ],
        }
    }, [legend, hoveredIndex])

    const options = useMemo<ChartOptions<"doughnut">>(
        () => ({
            responsive: true,
            maintainAspectRatio: true,
            cutout: "68%",
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
            },
            onHover: (_event, elements) => {
                onHoverIndex(elements.length > 0 ? elements[0].index : null)
            },
        }),
        [onHoverIndex],
    )

    return (
        <Box className={classes.wrapper}>
            <Doughnut data={data} options={options} />
            <Stack alignItems="center" justifyContent="center" className={classes.center}>
                <Typography component="span" className={classes.centerValue}>
                    {centerValue}
                </Typography>
                <LocalizedStrict id="service-center-usage-per-tenant-summary-center-caption">
                    <Typography variant="caption" className={classes.centerCaption}>
                        booked in period
                    </Typography>
                </LocalizedStrict>
            </Stack>
        </Box>
    )
}
